if (!customElements.get('cursist-drawer')) {
  customElements.define('cursist-drawer', class CursistDrawer extends HTMLElement {
    static DEFERRED_PROPERTY = 'Later aanleveren';
    static DEFERRED_ATTRIBUTE = 'Cursistengegevens later';

    constructor() {
      super();
      this.onBodyClick = this.handleBodyClick.bind(this);
      this.onKeyUp = this.handleKeyUp.bind(this);
      this.variantId = null;
      this.quantity = 1;
      this.attachDraagpas = false;
      this.pasVariantId = null;
      this.lineKey = null;
      this.cursistNumber = null;
      this.cartItems = null;
      this.mode = null;
      this.editItems = [];
      this.focusIndex = 0;
      this.fieldsRequired = false;
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    }

    connectedCallback() {
      this.dialog = this.querySelector('.cursist-drawer');
      this.list = this.querySelector('[data-cursist-drawer-list]');
      this.template = this.querySelector('[data-cursist-drawer-template]');
      this.form = this.querySelector('[data-cursist-drawer-form]');
      this.submitButton = this.querySelector('[data-cursist-drawer-submit]');
      this.submitLabel = this.submitButton?.querySelector('span:not(.visually-hidden)');
      this.provideLaterButton = this.querySelector('[data-cursist-provide-later]');
      this.progressText = this.querySelector('[data-cursist-progress-text]');
      this.progressBar = this.querySelector('[data-cursist-progress-bar]');
      this.progressFill = this.querySelector('[data-cursist-progress-fill]');
      this.productTitleEl = this.querySelector('[data-product-title]');
      this.overlay = this.nextElementSibling?.classList?.contains('cursist-drawer-bg')
        ? this.nextElementSibling
        : document.querySelector('.cursist-drawer-bg');

      this.labelCursist = this.dataset.labelCursist || 'Cursist';
      this.labelEmpty = this.dataset.labelEmpty || '';
      this.labelDeferred = this.dataset.labelDeferred || 'Wordt later aangeleverd';
      this.labelProgress = this.dataset.labelProgress || '{{ filled }} / {{ total }}';
      this.labelAdd = this.dataset.labelAdd || '';
      this.labelSave = this.dataset.labelSave || this.labelAdd;
      this.labelCheckout = this.dataset.labelCheckout || this.labelSave;
      this.introEl = this.querySelector('[data-cursist-drawer-intro]');
      this.introDefault = this.dataset.intro || this.introEl?.textContent || '';
      this.introRequired = this.dataset.introRequired || this.introDefault;

      this.querySelectorAll('[data-cursist-drawer-close]').forEach((button) => {
        button.addEventListener('click', this.close.bind(this));
      });

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.form.addEventListener('keydown', this.onFormKeyDown.bind(this));
      this.submitButton?.addEventListener('keydown', this.onFormKeyDown.bind(this));
      this.form.addEventListener('input', this.onFieldInput.bind(this));
      this.list.addEventListener('toggle', this.onAccordionToggle.bind(this), true);
      this.provideLaterButton?.addEventListener('click', this.onProvideLater.bind(this));
    }

    open({
      variantId,
      quantity,
      productTitle,
      attachDraagpas,
      pasVariantId,
      lineKey,
      cursistNumber,
      cartItems,
      initialValues,
      mode,
      items,
      focusIndex
    } = {}) {
      this.variantId = variantId;
      this.quantity = Math.max(1, parseInt(quantity, 10) || 1);
      this.attachDraagpas = Boolean(attachDraagpas);
      this.pasVariantId = pasVariantId || null;
      this.lineKey = lineKey || null;
      this.cursistNumber = cursistNumber ? parseInt(cursistNumber, 10) : null;
      this.cartItems = cartItems || document.querySelector('cart-items') || null;
      this.initialValues = initialValues || null;
      this.mode = mode || null;
      this.editItems = Array.isArray(items) ? items : [];
      this.focusIndex = Math.max(0, parseInt(focusIndex, 10) || 0);
      this.fieldsRequired = this.mode === 'checkout';

      if (this.productTitleEl) {
        this.productTitleEl.textContent = productTitle || '';
        this.productTitleEl.hidden = !productTitle;
      }

      if (this.mode === 'checkout') {
        this.setSubmitLabel(this.labelCheckout);
        this.buildEditGroupRows(this.editItems, this.focusIndex);
      } else if (this.mode === 'edit-group') {
        this.setSubmitLabel(this.labelSave);
        this.buildEditGroupRows(this.editItems, this.focusIndex);
      } else {
        this.setSubmitLabel(this.labelAdd);
        const rowSeed = this.initialValues != null
          ? this.initialValues
          : (this.lineKey ? {} : null);
        this.buildRows(this.quantity, rowSeed);
      }

      this.setFieldsRequired(this.fieldsRequired);
      this.setProvideLaterVisible(true);

      this.classList.add('active');
      this.dialog.classList.add('animate');
      this.dialog.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keyup', this.onKeyUp);

      // Display first, then slide in; defer body click so open click does not close
      requestAnimationFrame(() => {
        this.dialog.classList.add('active');
        document.body.addEventListener('click', this.onBodyClick);
        this.dialog.focus();
        trapFocus(this.dialog);
        const focusRow = (this.mode === 'edit-group' || this.mode === 'checkout')
          ? this.list.querySelector(`[data-cursist-index="${this.focusIndex}"]`)
          : this.list.querySelector('[data-cursist-index]');
        const firstEmpty = focusRow && Array.from(focusRow.querySelectorAll('[data-field]'))
          .find((input) => !input.value.trim());
        const firstInput = firstEmpty || focusRow?.querySelector('input') || this.list.querySelector('input');
        if (firstInput) firstInput.focus();
      });
    }

    close() {
      this.classList.remove('active');
      this.dialog.classList.remove('active');
      this.dialog.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.body.removeEventListener('click', this.onBodyClick);
      document.removeEventListener('keyup', this.onKeyUp);
      removeTrapFocus(this.activeElement);
      this.attachDraagpas = false;
      this.pasVariantId = null;
      this.lineKey = null;
      this.cursistNumber = null;
      this.initialValues = null;
      this.mode = null;
      this.editItems = [];
      this.focusIndex = 0;
      this.setFieldsRequired(false);
      this.setProvideLaterVisible(false);
      this.setSubmitLabel(this.labelAdd);

      const onTransitionEnd = (event) => {
        if (event.target !== this.dialog || event.propertyName !== 'transform') return;
        this.dialog.classList.remove('animate');
        this.dialog.removeEventListener('transitionend', onTransitionEnd);
      };

      this.dialog.addEventListener('transitionend', onTransitionEnd);
    }

    setSubmitLabel(label) {
      if (!this.submitLabel || !label) return;
      this.submitLabel.textContent = label;
    }

    setProvideLaterVisible(visible) {
      if (!this.provideLaterButton) return;
      this.provideLaterButton.hidden = !visible;
    }

    setIntro(required) {
      if (!this.introEl) return;
      this.introEl.textContent = required ? this.introRequired : this.introDefault;
    }

    setFieldsRequired(required) {
      this.fieldsRequired = Boolean(required);
      this.classList.toggle('cursist-drawer--required', this.fieldsRequired);
      this.dialog?.classList.toggle('cursist-drawer--required', this.fieldsRequired);
      this.list?.querySelectorAll('[data-field]').forEach((input) => {
        input.toggleAttribute('required', this.fieldsRequired);
        input.setAttribute('aria-required', this.fieldsRequired ? 'true' : 'false');
        input.classList.remove('is-invalid');
        input.removeAttribute('aria-invalid');
      });
      this.setIntro(this.fieldsRequired);
    }

    handleBodyClick(event) {
      if (event.target === this.overlay || event.target.classList?.contains('cursist-drawer-bg')) {
        this.close();
      }
    }

    handleKeyUp(event) {
      if (event.code === 'Escape') this.close();
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    buildRows(quantity, presetValues) {
      let previousValues;
      if (presetValues != null) {
        previousValues = Array.isArray(presetValues) ? presetValues : [presetValues];
      } else {
        previousValues = this.collectRowValues();
      }
      this.list.innerHTML = '';

      for (let index = 0; index < quantity; index++) {
        const clone = this.template.content.cloneNode(true);
        const details = clone.querySelector('details');
        const number = this.cursistNumber || (index + 1);
        const saved = previousValues[index] || {};

        details.dataset.cursistIndex = String(index);
        details.dataset.cursistNumber = String(number);
        if (this.lineKey) details.dataset.lineKey = this.lineKey;
        if (index === 0) details.open = true;
        if (saved.deferred || saved[this.constructor.DEFERRED_PROPERTY] === 'Ja') {
          details.dataset.deferred = 'true';
        }

        const numberEl = details.querySelector('[data-cursist-number]');
        const titleEl = details.querySelector('[data-cursist-title]');
        numberEl.textContent = String(number);
        titleEl.textContent = `${this.labelCursist} ${number}`;

        details.querySelectorAll('[data-field]').forEach((input) => {
          const field = input.dataset.field;
          const inputId = `Cursist-${field}-${number}-${this.dataset.section || 'drawer'}`;
          input.id = inputId;
          input.value = saved[field] || '';

          const label = details.querySelector(`[data-label-for="${field}"]`);
          if (label) label.setAttribute('for', inputId);
        });

        this.list.appendChild(clone);
        this.updateRowState(this.list.querySelector(`[data-cursist-index="${index}"]`));
      }

      this.updateProgress();
    }

    buildEditGroupRows(items, focusIndex) {
      this.list.innerHTML = '';
      const mixedTitles = new Set(items.map((item) => item.productTitle).filter(Boolean)).size > 1;

      items.forEach((item, index) => {
        const clone = this.template.content.cloneNode(true);
        const details = clone.querySelector('details');
        const number = item.number || (index + 1);

        details.dataset.cursistIndex = String(index);
        details.dataset.lineKey = item.lineKey || '';
        details.dataset.cursistNumber = String(number);
        details.open = index === focusIndex;
        if (item.deferred) details.dataset.deferred = 'true';

        const numberEl = details.querySelector('[data-cursist-number]');
        const titleEl = details.querySelector('[data-cursist-title]');
        numberEl.textContent = String(number);
        titleEl.textContent = mixedTitles && item.productTitle
          ? `${item.productTitle} · ${this.labelCursist} ${number}`
          : `${this.labelCursist} ${number}`;

        details.querySelectorAll('[data-field]').forEach((input) => {
          const field = input.dataset.field;
          const inputId = `Cursist-${field}-${number}-${this.dataset.section || 'drawer'}-edit`;
          input.id = inputId;
          input.value = item[field] || '';

          const label = details.querySelector(`[data-label-for="${field}"]`);
          if (label) label.setAttribute('for', inputId);
        });

        this.list.appendChild(clone);
        this.updateRowState(this.list.querySelector(`[data-cursist-index="${index}"]`));
      });

      this.updateProgress();
    }

    buildProperties() {
      const values = this.collectRowValues()[0] || {};
      return this.buildPropertiesFromValues(values, this.cursistNumber || 1, values.deferred);
    }

    buildPropertiesFromRow(row) {
      const values = this.getRowValues(row);
      const deferred = row.dataset.deferred === 'true';
      return this.buildPropertiesFromValues(
        values,
        row.dataset.cursistNumber || 1,
        deferred
      );
    }

    buildPropertiesFromValues(values, cursistNumber, deferred = false) {
      const properties = {
        _cursist: String(cursistNumber)
      };

      Object.entries(values).forEach(([key, value]) => {
        if (key === 'deferred') return;
        if (value) properties[key] = value;
      });

      if (deferred) {
        properties[this.constructor.DEFERRED_PROPERTY] = 'Ja';
      }

      return properties;
    }

    collectRowValues() {
      return Array.from(this.list.querySelectorAll('[data-cursist-index]')).map((row) => {
        const values = this.getRowValues(row);
        values.deferred = row.dataset.deferred === 'true';
        return values;
      });
    }

    fieldsChanged(original, current, deferred) {
      const fields = ['Voornaam', 'Achternaam', 'Bedrijf', 'Email'];
      const fieldChanged = fields.some((field) => (original?.[field] || '') !== (current?.[field] || ''));
      const deferredChanged = Boolean(original?.deferred) !== Boolean(deferred);
      return fieldChanged || deferredChanged;
    }

    collectEditedUpdates({ forceDeferredIncomplete = false } = {}) {
      return Array.from(this.list.querySelectorAll('[data-cursist-index]')).reduce((updates, row, index) => {
        const lineKey = row.dataset.lineKey;
        if (!lineKey) return updates;

        if (forceDeferredIncomplete && !this.isRowFullyFilled(this.getRowValues(row))) {
          row.dataset.deferred = 'true';
        }

        const original = this.editItems[index] || {};
        const current = this.getRowValues(row);
        const deferred = row.dataset.deferred === 'true';
        const shouldForceDeferred = forceDeferredIncomplete && deferred;
        if (!this.fieldsChanged(original, current, deferred) && !shouldForceDeferred) return updates;

        this.updateRowState(row);

        updates.push({
          key: lineKey,
          properties: this.buildPropertiesFromRow(row)
        });
        return updates;
      }, []);
    }

    onFieldInput(event) {
      const input = event.target;
      if (input.matches?.('[data-field]') && this.fieldsRequired) {
        const invalid = this.isFieldInvalid(input);
        input.classList.toggle('is-invalid', input.classList.contains('is-invalid') && invalid);
        if (!invalid) input.removeAttribute('aria-invalid');
      }

      const row = event.target.closest('[data-cursist-index]');
      if (!row) return;

      if (row.dataset.deferred === 'true' && this.isRowFullyFilled(this.getRowValues(row))) {
        delete row.dataset.deferred;
      }

      this.updateRowState(row);
      this.updateProgress();
    }

    onFormKeyDown(event) {
      if (event.key !== 'Enter') return;

      event.preventDefault();

      const rows = Array.from(this.list.querySelectorAll('[data-cursist-index]'));
      if (!rows.length) return;

      const allComplete = rows.every((row) => this.isRowComplete(row));
      if (allComplete) {
        this.form.requestSubmit(this.submitButton);
        return;
      }

      this.focusNextCursist(event.target);
    }

    focusNextCursist(fromElement) {
      const rows = Array.from(this.list.querySelectorAll('[data-cursist-index]'));
      if (!rows.length) return;

      const currentRow = fromElement?.closest?.('[data-cursist-index]');
      const currentIndex = currentRow
        ? rows.indexOf(currentRow)
        : -1;

      let nextRow = null;
      for (let offset = 1; offset <= rows.length; offset++) {
        const candidate = rows[(currentIndex + offset) % rows.length];
        if (!this.isRowComplete(candidate)) {
          nextRow = candidate;
          break;
        }
      }

      if (!nextRow) nextRow = rows[(currentIndex + 1) % rows.length];

      rows.forEach((row) => {
        row.open = row === nextRow;
      });

      const firstEmpty = Array.from(nextRow.querySelectorAll('[data-field]'))
        .find((input) => !input.value.trim());
      const focusInput = firstEmpty || nextRow.querySelector('[data-field]');
      if (focusInput) focusInput.focus();
    }

    onAccordionToggle(event) {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;

      this.list.querySelectorAll('details[data-cursist-index]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }

    getRowValues(row) {
      const values = {};
      row.querySelectorAll('[data-field]').forEach((input) => {
        values[input.dataset.field] = input.value.trim();
      });
      return values;
    }

    isRowFullyFilled(values) {
      const filled = Boolean(values.Voornaam && values.Achternaam && values.Bedrijf && values.Email);
      if (!filled) return false;
      if (!this.isValidEmail(values.Email)) return false;
      return true;
    }

    isRowComplete(rowOrValues) {
      const isRow = rowOrValues?.querySelectorAll != null;
      const values = isRow ? this.getRowValues(rowOrValues) : rowOrValues;
      if (isRow && rowOrValues.dataset.deferred === 'true') return true;
      if (!isRow && values?.deferred) return true;
      return this.isRowFullyFilled(values);
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }

    isFieldInvalid(input) {
      const value = input.value.trim();
      if (!value) return true;
      if (input.dataset.field === 'Email' && !this.isValidEmail(value)) return true;
      return false;
    }

    areAllRowsComplete() {
      const rows = Array.from(this.list.querySelectorAll('[data-cursist-index]'));
      return rows.length > 0 && rows.every((row) => this.isRowComplete(row));
    }

    hasDeferredRows() {
      return Array.from(this.list.querySelectorAll('[data-cursist-index]'))
        .some((row) => row.dataset.deferred === 'true' && !this.isRowFullyFilled(this.getRowValues(row)));
    }

    revealIncompleteFields() {
      const rows = Array.from(this.list.querySelectorAll('[data-cursist-index]'));
      let firstIncomplete = null;

      rows.forEach((row) => {
        if (row.dataset.deferred === 'true') return;

        const complete = this.isRowComplete(row);
        row.querySelectorAll('[data-field]').forEach((input) => {
          const invalid = this.isFieldInvalid(input);
          input.classList.toggle('is-invalid', invalid);
          input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
        });
        if (!complete && !firstIncomplete) firstIncomplete = row;
      });

      if (!firstIncomplete) return;

      rows.forEach((row) => {
        row.open = row === firstIncomplete;
      });

      const firstEmpty = Array.from(firstIncomplete.querySelectorAll('[data-field]'))
        .find((input) => this.isFieldInvalid(input));
      if (firstEmpty) firstEmpty.focus();
    }

    updateRowState(row) {
      if (!row) return;

      const values = this.getRowValues(row);
      const deferred = row.dataset.deferred === 'true' && !this.isRowFullyFilled(values);
      const complete = this.isRowFullyFilled(values);
      const meta = row.querySelector('[data-cursist-meta]');
      const numberEl = row.querySelector('[data-cursist-number]');
      const checkEl = row.querySelector('[data-cursist-check]');

      if (deferred) {
        row.dataset.deferred = 'true';
      } else {
        delete row.dataset.deferred;
      }

      row.classList.toggle('cursist-drawer__item--complete', complete);
      row.classList.toggle('cursist-drawer__item--deferred', deferred);
      numberEl.hidden = complete;
      checkEl.hidden = !complete;

      if (complete) {
        const name = [values.Voornaam, values.Achternaam].filter(Boolean).join(' ');
        meta.textContent = [name, values.Bedrijf, values.Email].filter(Boolean).join(' | ');
      } else if (deferred) {
        meta.textContent = this.labelDeferred;
      } else {
        meta.textContent = this.labelEmpty;
      }
    }

    updateProgress() {
      const rows = Array.from(this.list.querySelectorAll('[data-cursist-index]'));
      const total = rows.length || 1;
      const filled = rows.filter((row) => this.isRowComplete(row)).length;
      const percent = Math.round((filled / total) * 100);

      this.progressText.textContent = this.labelProgress
        .replace('{{ filled }}', String(filled))
        .replace('{{ total }}', String(total));
      this.progressFill.style.width = `${percent}%`;
      this.progressBar.setAttribute('aria-valuenow', String(percent));
    }

    buildItems() {
      return this.collectRowValues().map((values, index) => {
        return {
          id: Number(this.variantId),
          quantity: 1,
          properties: this.buildPropertiesFromValues(values, index + 1, values.deferred)
        };
      });
    }

    getCartPageSections() {
      if (!this.cartItems?.getSectionsToRender) return null;
      return this.cartItems.getSectionsToRender().map((section) => section.section);
    }

    extractAddedKeys(response) {
      const items = response.items || (response.key ? [response] : []);
      return items.map((item) => item.key).filter(Boolean);
    }

    addNestedDraagpas(parentKeys, sections) {
      const items = parentKeys.map((key) => ({
        id: Number(this.pasVariantId),
        quantity: 1,
        parent_line_key: key
      }));

      const body = {
        items,
        sections_url: window.location.pathname
      };
      if (sections) body.sections = sections;

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = JSON.stringify(body);
      config.headers['Content-Type'] = 'application/json';

      return fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
    }

    renderAfterAdd(response) {
      if (this.cartItems?.renderContents && response.sections) {
        this.cartItems.renderContents(response);
        return;
      }

      if (this.cart) {
        this.cart.renderContents(response);
        return;
      }

      window.location = window.routes.cart_url;
    }

    syncDeferredCartAttribute(hasDeferred) {
      const value = hasDeferred ? 'Ja' : '';
      const body = {
        attributes: {
          [this.constructor.DEFERRED_ATTRIBUTE]: value
        }
      };

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = JSON.stringify(body);
      config.headers['Content-Type'] = 'application/json';

      return fetch(`${routes.cart_update_url || '/cart/update.js'}`, config)
        .then((response) => response.json())
        .then((state) => {
          this.cartItems?.syncDeferredCheckoutFields?.(hasDeferred, state);
          return state;
        });
    }

    setLoading(isLoading) {
      if (!this.submitButton) return;
      const spinner = this.submitButton.querySelector('.loading-overlay__spinner');

      if (isLoading) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.provideLaterButton?.setAttribute('aria-disabled', true);
        if (spinner) spinner.classList.remove('hidden');
        return;
      }

      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-disabled');
      this.provideLaterButton?.removeAttribute('aria-disabled');
      if (spinner) spinner.classList.add('hidden');
    }

    markIncompleteRowsDeferred() {
      this.list.querySelectorAll('[data-cursist-index]').forEach((row) => {
        if (!this.isRowFullyFilled(this.getRowValues(row))) {
          row.dataset.deferred = 'true';
          this.updateRowState(row);
        }
      });
      this.updateProgress();
    }

    collectDeferredLineUpdates() {
      this.markIncompleteRowsDeferred();

      const updates = [];
      const seen = new Set();

      const pushUpdate = (key, properties) => {
        if (!key || seen.has(key)) return;
        seen.add(key);
        updates.push({ key, properties });
      };

      this.collectEditedUpdates({ forceDeferredIncomplete: true }).forEach((update) => {
        pushUpdate(update.key, update.properties);
      });

      this.list.querySelectorAll('[data-cursist-index]').forEach((row) => {
        if (row.dataset.deferred !== 'true') return;
        pushUpdate(row.dataset.lineKey || this.lineKey, this.buildPropertiesFromRow(row));
      });

      if (!updates.length && this.lineKey) {
        pushUpdate(this.lineKey, this.buildProperties());
      }

      return updates;
    }

    deferExistingCartLines({ proceedToCheckout = false } = {}) {
      if (!this.cartItems?.updateMultipleLineProperties && !this.cartItems?.changeLineProperties) {
        return Promise.reject(new Error('Cart updates unavailable'));
      }

      const updates = this.collectDeferredLineUpdates();

      const runUpdates = () => {
        if (!updates.length) return Promise.resolve();
        if (updates.length === 1 && this.cartItems.changeLineProperties) {
          return this.cartItems.changeLineProperties(updates[0].key, updates[0].properties, {
            render: !proceedToCheckout
          });
        }
        return this.cartItems.updateMultipleLineProperties(updates, { render: !proceedToCheckout });
      };

      return runUpdates()
        .then(() => this.syncDeferredCartAttribute(true))
        .then(() => {
          this.close();
          if (proceedToCheckout) this.cartItems.proceedToCheckout();
        });
    }

    finishCheckoutFlow(updates, hasDeferred) {
      const runUpdates = () => {
        if (!updates.length) return Promise.resolve();
        return this.cartItems.updateMultipleLineProperties(updates, { render: false });
      };

      return runUpdates()
        .then(() => this.syncDeferredCartAttribute(hasDeferred))
        .then(() => {
          this.close();
          this.cartItems.proceedToCheckout();
        });
    }

    addDeferredItemsToCart() {
      this.markIncompleteRowsDeferred();

      if (!this.variantId) {
        return Promise.reject(new Error('Missing variant for deferred add'));
      }

      const cartPageSections = this.getCartPageSections();
      const body = {
        items: this.buildItems(),
        sections_url: window.location.pathname
      };

      if (cartPageSections) {
        body.sections = cartPageSections;
      } else if (this.cart?.getSectionsToRender) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = JSON.stringify(body);
      config.headers['Content-Type'] = 'application/json';

      return fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.error(response.description || response.message);
            return null;
          }

          if (this.attachDraagpas && this.pasVariantId) {
            const newKeys = this.extractAddedKeys(response);
            if (newKeys.length) {
              return this.addNestedDraagpas(newKeys, cartPageSections || body.sections).then((pasResponse) => {
                if (pasResponse?.status) {
                  console.error(pasResponse.description || pasResponse.message);
                  return response;
                }
                return pasResponse?.sections ? pasResponse : response;
              });
            }
          }

          return response;
        })
        .then((response) => {
          if (!response) return null;
          return this.syncDeferredCartAttribute(true).then(() => response);
        })
        .then((response) => {
          if (!response) return;
          this.close();
          window.location = window.routes.cart_url;
        });
    }

    onProvideLater(event) {
      event.preventDefault();
      if (this.submitButton?.classList.contains('loading')) return;

      this.setLoading(true);

      const fromCheckout = this.mode === 'checkout';
      const onCart = Boolean(
        this.cartItems &&
        (fromCheckout || this.mode === 'edit-group' || this.lineKey)
      );

      const action = onCart
        ? this.deferExistingCartLines({ proceedToCheckout: fromCheckout })
        : this.addDeferredItemsToCart();

      action
        .catch((error) => {
          console.error(error);
        })
        .finally(() => this.setLoading(false));
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.submitButton.classList.contains('loading')) return;

      if (this.fieldsRequired && !this.areAllRowsComplete()) {
        this.revealIncompleteFields();
        return;
      }

      const shouldCheckout = this.mode === 'checkout';
      const isGroupEdit = this.mode === 'edit-group' || shouldCheckout;

      this.setLoading(true);

      if (isGroupEdit && this.cartItems?.updateMultipleLineProperties) {
        const updates = this.collectEditedUpdates();
        const hasDeferred = this.hasDeferredRows();

        if (shouldCheckout) {
          this.finishCheckoutFlow(updates, hasDeferred)
            .catch((error) => {
              console.error(error);
            })
            .finally(() => this.setLoading(false));
          return;
        }

        if (!updates.length) {
          this.close();
          this.setLoading(false);
          return;
        }

        this.cartItems.updateMultipleLineProperties(updates, { render: true })
          .then(() => this.syncDeferredCartAttribute(hasDeferred))
          .then(() => {
            this.close();
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => this.setLoading(false));
        return;
      }

      if (this.lineKey && this.cartItems?.changeLineProperties) {
        this.cartItems.changeLineProperties(this.lineKey, this.buildProperties())
          .then(() => this.syncDeferredCartAttribute(this.hasDeferredRows()))
          .then(() => {
            this.close();
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => this.setLoading(false));
        return;
      }

      const cartPageSections = this.getCartPageSections();
      const body = {
        items: this.buildItems(),
        sections_url: window.location.pathname
      };

      if (cartPageSections) {
        body.sections = cartPageSections;
      } else if (this.cart?.getSectionsToRender) {
        body.sections = this.cart.getSectionsToRender().map((section) => section.id);
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = JSON.stringify(body);
      config.headers['Content-Type'] = 'application/json';

      const hasDeferred = this.hasDeferredRows();

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.error(response.description || response.message);
            return null;
          }

          if (this.attachDraagpas && this.pasVariantId) {
            const newKeys = this.extractAddedKeys(response);
            if (newKeys.length) {
              return this.addNestedDraagpas(newKeys, cartPageSections || body.sections).then((pasResponse) => {
                if (pasResponse?.status) {
                  console.error(pasResponse.description || pasResponse.message);
                  return response;
                }
                return pasResponse?.sections ? pasResponse : response;
              });
            }
          }

          return response;
        })
        .then((response) => {
          if (!response) return null;
          if (!hasDeferred) return response;
          return this.syncDeferredCartAttribute(true).then(() => response);
        })
        .then((response) => {
          if (!response) return;

          this.close();
          this.renderAfterAdd(response);
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => this.setLoading(false));
    }
  });
}
